import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import { Quote } from '../../../../../../../Typescript/backend/classes';
import { GetAllQuoteEditionsQuery } from '../../../../../../../gql/graphql';
import { isNotNullish, sortByFriendlyId } from '../../../../../../../util';
import {
  GET_ALL_QUOTE_EDITIONS,
  GET_QUOTE,
} from '../../../../../../../queries';
import { styled } from '@mui/material';
import LoadingScreen from '../../../../LoadingScreen';
import { CoveragesAccordion } from './CoveragesAccordion';
import { AnswersAccordion } from './AnswersAccordion';

interface QuoteEditionsComparisonProps {
  quote?: Quote;
}

type QuoteEdition = ArrayElement<
  NonNullable<GetAllQuoteEditionsQuery['getAllQuoteEditions']>
>;

export const QuoteEditionsComparison = ({
  quote,
}: QuoteEditionsComparisonProps) => {
  const { token } = useAuth();
  const [quoteEditions, setQuoteEditions] = useState<Array<QuoteEdition>>([]);
  const [comparisonQuote, setComparisonQuote] = useState<Quote | null>(null);
  const [selectedEditionId, setSelectedEditionId] = useState<string>('');
  const [getAllQuoteEditionsQuery, { data, loading }] = useLazyQuery(
    GET_ALL_QUOTE_EDITIONS,
  );
  const [
    getComparisonQuote,
    { data: comparisonQuoteData, loading: comparisonLoading },
  ] = useLazyQuery(GET_QUOTE);

  useEffect(() => {
    if (quote?.uniqueId && token) {
      getAllQuoteEditionsQuery({
        variables: {
          token,
          quoteUuid: quote.uniqueId,
        },
      });
    }
  }, [quote?.uniqueId, token, getAllQuoteEditionsQuery]);

  useEffect(() => {
    if (data?.getAllQuoteEditions) {
      const filteredEditions = [...data.getAllQuoteEditions].filter(
        isNotNullish,
      );
      const editions = filteredEditions.sort(sortByFriendlyId);
      setQuoteEditions(editions);
    }
  }, [data]);

  useEffect(() => {
    if (selectedEditionId && token) {
      getComparisonQuote({
        variables: {
          uID: selectedEditionId,
          token,
        },
      });
    } else {
      setComparisonQuote(null);
    }
  }, [selectedEditionId, token, getComparisonQuote]);

  useEffect(() => {
    if (comparisonQuoteData?.quote && selectedEditionId) {
      setComparisonQuote(
        Quote.generateFromBackendResponse(comparisonQuoteData.quote),
      );
    } else if (!selectedEditionId) {
      setComparisonQuote(null);
    }
  }, [comparisonQuoteData, selectedEditionId]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (data && quoteEditions.length === 1) {
    return <NoEditionsNotice />;
  }

  return (
    <StyledContainer>
      <EditionsContainer>
        <Typography variant='h5' component='h3' sx={{ marginBottom: 2 }}>
          Select Edition to Compare
        </Typography>
        <StyledButtonGroup variant='outlined' aria-label='quote editions'>
          {quoteEditions.map((edition, index) => {
            const isCurrentQuote = edition.uniqueID === quote?.uniqueId;
            const isSelected = edition.uniqueID === selectedEditionId;
            const buttonText = edition.friendlyId || `Edition ${index + 1}`;

            return (
              <Button
                key={edition.id}
                onClick={() =>
                  setSelectedEditionId((oldId) => {
                    return oldId === edition.uniqueID ? '' : edition.uniqueID;
                  })
                }
                disabled={isCurrentQuote}
                variant={isSelected ? 'contained' : 'outlined'}
                color={isSelected ? 'primary' : 'inherit'}
              >
                {buttonText}
              </Button>
            );
          })}
        </StyledButtonGroup>
      </EditionsContainer>

      <Collapse in={!!quote && !!comparisonQuote}>
        <CoveragesAccordion
          quote={quote}
          comparisonQuote={comparisonQuote}
          loading={comparisonLoading}
        />
        <AnswersAccordion
          quote={quote}
          comparisonQuote={comparisonQuote}
          loading={comparisonLoading}
        />
      </Collapse>
    </StyledContainer>
  );
};

const NoEditionsNotice = () => {
  return (
    <StyledNoEditionsBox>
      <Typography variant='body1'>
        There are no other editions to compare this quote to. These will become
        visible once the application has been renewed.
      </Typography>
    </StyledNoEditionsBox>
  );
};

const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StyledNoEditionsBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
}));

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => {
  return {
    padding: theme.spacing(2, 0),
    marginBottom: theme.spacing(3),
  };
});

const EditionsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));
